
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DatePicker from 'commons/components/DatePicker';
import ButtonWithLoader from 'commons/containers/ButtonWithLoader';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  container: {
    padding: theme.spacing(2),
  },
  filterButton: {
    marginTop: theme.spacing(2),
  },
}));

const defaultFilters = {
  fromDate: moment().subtract(1, 'month').toDate(),
  toDate: moment().toDate(),
  plant: null,
  processingSite: null,
  producer: null,
  loadCondition: null,
  carrier: null, // New carrier filter added
  operationType: null, // New operation type filter
  state: null,
  id: '',
};

function Filters(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { objects, actions, filters, loadConditions } = props;
  const [filter, setFilter] = useState(filters);

  React.useEffect(() => {
    setFilter(filters);
  }, [filters]);

  const handleFilterChange = (e, val) => {
    const id = e.target.id || e.currentTarget.id;
    const value = val !== undefined ? val : e.target.value;
    
    setFilter((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleSearch = () => {
    actions.setFilters(filter);
  };

  const clearFilters = () => {
    const defaults = {
        fromDate: moment().toDate(), // Default to Today as per controller logic, or keep the initial Logic
        toDate: moment().toDate(),
        plant: null,
        processingSite: null,
        producer: null,
        loadCondition: null,
        carrier: null,
        operationType: null,
        state: null,
        id: '',
    };
    setFilter(defaults);
    actions.setFilters(defaults);
  };

  const producerOptions = React.useMemo(() => {
    if (!objects || !objects.suppliers) return [];

    const grouped = Object.values(objects.suppliers).reduce((acc, supplier) => {
      const entity = objects.entities && objects.entities[supplier.entityId];
      const label = supplier.companyName || (entity && entity.name) || `Supplier ${supplier.id}`;
      
      if (!acc[label]) {
        acc[label] = {
          label,
          value: [],
        };
      }
      acc[label].value.push(supplier.id);
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.label.localeCompare(b.label));
  }, [objects]);

  const carrierOptions = React.useMemo(() => {
    if (!objects || !objects.carriers) return [];
    
    return Object.values(objects.carriers).map(carrier => ({
      value: carrier.id,
      label: carrier.name || `Transportista ${carrier.id}`,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [objects]);

  const processingSiteOptions = React.useMemo(() => {
    if (!objects || !objects.processingSites) return [];
    
    return Object.values(objects.processingSites).map(site => ({
      value: site.id,
      label: site.name || `Planta ${site.id}`,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [objects]);

  return (
    <Card className={classes.root}>
      <CardHeader title={t('Filter')} />
      <CardContent>
        <Grid container spacing={3} className={classes.container}>
          <Grid container item spacing={3} xs={12}>
            <Grid item sm={3} xs={12}>
              <DatePicker
                inputId="fromDate"
                label={t('From')}
                fullWidth
                value={filter.fromDate}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <DatePicker
                inputId="toDate"
                label={t('To')}
                fullWidth
                value={filter.toDate}
                onChange={handleFilterChange}
              />
            </Grid>

            <Grid item sm={3} xs={12}>
              <Autocomplete
                options={producerOptions}
                getOptionLabel={(option) => option.label || ''}
                value={
                   filter.producer && producerOptions.length > 0
                    ? producerOptions.find((p) => {
                         // Check if the current filter value matches or is included in the option's value array
                         if (Array.isArray(filter.producer)) {
                             // If filter is array, look for intersection or exact match
                             return p.value.some(r=> filter.producer.includes(r))
                         }
                         return p.value.includes(filter.producer);
                      }) || null
                    : null
                }
                getOptionSelected={(option, value) => {
                    // Compare if values overlap or match
                     if (!value) return false;
                     // Handled by the value finding logic mostly, but for internal equality:
                     return option.label === value.label
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('Producer')} />
                )}
                onChange={(e, val) =>
                  handleFilterChange({ target: { id: 'producer', value: val ? val.value : null } })
                }
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <Autocomplete
                options={loadConditions && loadConditions.length > 0 ? loadConditions : ['HUMEDO', 'SECO']}
                getOptionLabel={(option) => option.label || option}
                value={filter.loadCondition || null}
                renderInput={(params) => (
                  <TextField {...params} label={t('scheduleManagement.filters.loadCondition')} />
                )}
                onChange={(e, val) =>
                  handleFilterChange({ target: { id: 'loadCondition', value: val } })
                }
              />
            </Grid>
          </Grid>

          <Grid container item spacing={3} xs={12}>
            <Grid item sm={3} xs={12}>
              <Autocomplete
                options={carrierOptions}
                getOptionLabel={(option) => option.label || ''}
                value={
                   filter.carrier && carrierOptions.length > 0
                    ? carrierOptions.find((p) => p.value === filter.carrier) || null
                    : null
                }
                renderInput={(params) => (
                  <TextField {...params} label={t('Transportista')} />
                )}
                onChange={(e, val) =>
                  handleFilterChange({ target: { id: 'carrier', value: val && typeof val === 'object' && 'value' in val ? val.value : null } })
                }
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <Autocomplete
                options={[
                  { id: 'PICKUP', name: t('scheduleManagement.operationTypes.pickup') },
                  { id: 'DISPATCH', name: t('scheduleManagement.operationTypes.dispatch') },
                ]}
                getOptionLabel={(option) => option.name || ''}
                value={
                   filter.operationType
                    ? [{ id: 'PICKUP', name: t('scheduleManagement.operationTypes.pickup') }, { id: 'DISPATCH', name: t('scheduleManagement.operationTypes.dispatch') }].find((p) => p.id === filter.operationType) || null
                    : null
                }
                renderInput={(params) => (
                  <TextField {...params} label={t('scheduleManagement.form.operationType')} />
                )}
                onChange={(e, val) =>
                  handleFilterChange({ target: { id: 'operationType', value: val && typeof val === 'object' && 'id' in val ? val.id : null } })
                }
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <Autocomplete
                options={processingSiteOptions}
                getOptionLabel={(option) => option.label || ''}
                value={
                   filter.processingSite && processingSiteOptions.length > 0
                    ? processingSiteOptions.find((p) => p.value === filter.processingSite) || null
                    : null
                }
                renderInput={(params) => (
                  <TextField {...params} label={t('Planta')} />
                )}
                onChange={(e, val) =>
                  handleFilterChange({ target: { id: 'processingSite', value: val && typeof val === 'object' && 'value' in val ? val.value : null } })
                }
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <Autocomplete
                options={[
                  { id: 'PENDING', name: t('PENDIENTE') },
                  { id: 'COMPLETED', name: t('RECIBIDO') },
                ]}
                getOptionLabel={(option) => option.name || ''}
                value={
                   filter.state
                    ? [{ id: 'PENDING', name: t('PENDIENTE') }, { id: 'COMPLETED', name: t('RECIBIDO') }].find((p) => p.id === filter.state) || null
                    : null
                }
                renderInput={(params) => (
                  <TextField {...params} label={t('Estado')} />
                )}
                onChange={(e, val) =>
                  handleFilterChange({ target: { id: 'state', value: val && typeof val === 'object' && 'id' in val ? val.id : null } })
                }
              />
            </Grid>
          </Grid>

          <Grid container item spacing={3} alignItems="center" xs={12}>
            <Grid item sm={3} xs={12}>
              <TextField
                id="id"
                label={t('Buscar por ID')}
                value={filter.id || ''}
                onChange={handleFilterChange}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={3} style={{ marginLeft: 'auto' }}>
              <ButtonWithLoader
                fullWidth
                onClick={clearFilters}
                className={classes.filterButton}
                size="small">
                {t('Clear')}
              </ButtonWithLoader>
            </Grid>
            <Grid item xs={12} sm={3}>
              <ButtonWithLoader
                fullWidth
                className={classes.filterButton}
                size="small"
                onClick={handleSearch}
                startIcon={<SearchIcon />}>
                {t('Search')}
              </ButtonWithLoader>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default Filters;
